/**
 * Three-pane agent desktop.
 *
 * Left: assigned leads, callbacks and queue items.
 * Centre: the matched customer record, activity history and script.
 * Right: call controls, notes and disposition.
 * The persistent call bar lives in the shell so it survives route changes.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSession } from '@/app/session';
import { api } from '@/lib/api/client';
import type { ActivityEntry, ProviderCapabilities } from '@/lib/api/types';
import { formatDateTime, formatDuration, titleCase } from '@/lib/format';
import { Badge, Button, EmptyState, Field, LoadingState, Panel, Row } from '@/ui/primitives';
import { CallBar, isTerminalCallStatus } from './CallBar';
import { controlAvailability } from './callControls';
import { useAgentWorkspace } from './useAgentWorkspace';
import { useTwilioDevice } from './twilioDevice';

const BREAK_STATES = ['break', 'lunch', 'meeting', 'training', 'coaching', 'technical_issue'];
type MobileSection = 'dial' | 'leads' | 'call' | 'finish';
type ExpandedPanel = 'leads' | 'recent' | null;

export function AgentWorkspace() {
  const { user } = useSession();
  const { state, actions } = useAgentWorkspace();
  const [providerCaps, setProviderCaps] = useState<string[]>([]);
  const [providerName, setProviderName] = useState('Provider');
  const [providerRaw, setProviderRaw] = useState('');
  const [notes, setNotes] = useState('');
  const [leadNote, setLeadNote] = useState('');
  const [savingLeadNote, setSavingLeadNote] = useState(false);
  const [dispositionId, setDispositionId] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const [transferTarget, setTransferTarget] = useState('');
  const [consultLegId, setConsultLegId] = useState<string | null>(null);
  const [outboundNumber, setOutboundNumber] = useState('');
  const [dialValidation, setDialValidation] = useState('');
  const [mobileSection, setMobileSection] = useState<MobileSection>('dial');
  const [expandedPanel, setExpandedPanel] = useState<ExpandedPanel>(null);

  useEffect(() => {
    api
      .get<ProviderCapabilities>('/agent/capabilities')
      .then((caps) => {
        setProviderCaps(caps.capabilities ?? []);
        setProviderName(titleCase(caps.name));
        setProviderRaw(caps.name ?? '');
      })
      .catch(() => {
        setProviderCaps([]);
        setProviderName('Provider');
        setProviderRaw('');
      });
  }, []);

  // Only registers a Twilio Voice Device (fetches a token, requests
  // microphone access) when this organization is actually on Twilio. See
  // twilioDevice.ts — it silently bridges audio for whatever call state
  // useAgentWorkspace's own actions already produced; it never drives its
  // own call UI.
  const voiceDevice = useTwilioDevice(providerRaw === 'twilio');

  const controlContext = useMemo(
    () => ({
      agentState: state.agent?.state ?? null,
      call: state.call,
      providerCapabilities: providerCaps,
      userCapabilities: user?.capabilities ?? [],
      hasDisposition: Boolean(dispositionId),
      conferenceLegCount: state.call?.legs.filter((leg) => !leg.status.includes('completed')).length ?? 0,
    }),
    [state.agent?.state, state.call, providerCaps, user?.capabilities, dispositionId],
  );

  // Keyboard shortcuts mirror the call-bar buttons exactly.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const map: Record<string, () => void> = {
        F1: actions.answer,
        F2: state.agent?.state === 'hold' ? actions.resume : actions.hold,
        F4: actions.hangup,
        F8: actions.completeAcw,
      };
      const action = map[event.key];
      if (action) {
        event.preventDefault();
        action();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [actions, state.agent?.state]);

  const selectedDisposition = state.dispositions.find((item) => item.id === dispositionId);
  const liveCustomerNumber = state.call
    ? state.call.direction === 'outbound'
      ? state.call.to_e164
      : state.call.from_e164
    : '';
  const callIsLive = Boolean(state.call && !isTerminalCallStatus(state.call.status));

  useEffect(() => {
    if (callIsLive) setMobileSection('call');
    else if (state.agent?.state === 'acw') setMobileSection('finish');
  }, [callIsLive, state.agent?.state, state.call?.id]);
  const liveRecordingStatus = state.call
    ? !providerCaps.includes('recording')
      ? `Not available with ${providerName}`
      : state.call.recording.available
        ? `${titleCase(state.call.recording.status)} · ${formatDuration(state.call.recording.duration_seconds)}`
        : state.call.recording.enabled
          ? titleCase(state.call.recording.status)
          : 'Disabled'
    : '';
  const canPlaceOutbound =
    providerCaps.includes('place_call') &&
    state.agent?.state === 'available' &&
    !state.agent.current_call_id;

  // Browser calling adds a real failure mode plain server-side dialling
  // doesn't have: the provider can be fully configured while this specific
  // browser tab never actually got microphone/WebRTC audio working. Folding
  // voiceDevice's status into the same badge means "ready" only shows once
  // both are true, instead of a server-only check quietly lying to a Twilio
  // agent whose Device failed to register.
  const usesBrowserVoice = providerRaw === 'twilio';
  const dialerBadge = !providerCaps.includes('place_call')
    ? { tone: 'warn' as const, label: 'Unavailable' }
    : !usesBrowserVoice
      ? { tone: 'ok' as const, label: `${providerName} ready` }
      : voiceDevice.status === 'ready'
        ? { tone: 'ok' as const, label: `${providerName} ready` }
        : voiceDevice.status === 'error'
          ? { tone: 'warn' as const, label: 'Browser audio unavailable' }
          : { tone: 'warn' as const, label: 'Connecting browser audio…' };

  const submitOutbound = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const phone = outboundNumber.trim();
    if (!/^\+[1-9]\d{6,14}$/.test(phone)) {
      setDialValidation('Use international format, for example +14155550123');
      return;
    }
    setDialValidation('');
    void actions.dial(phone);
  };
  const recentCallsForPanel = useMemo(() => {
    const visible = state.recentCalls.slice(0, 8);
    const hasInbound = visible.some((call) => call.direction === 'inbound');
    const hasOutbound = visible.some((call) => call.direction === 'outbound');
    const missingDirection = !hasInbound ? 'inbound' : !hasOutbound ? 'outbound' : null;
    const missingCall = missingDirection
      ? state.recentCalls.find((call) => call.direction === missingDirection)
      : null;

    if (!missingCall || visible.some((call) => call.id === missingCall.id)) return visible;
    const newestCall = visible[0];
    if (!newestCall) return [missingCall];
    return [newestCall, missingCall, ...visible.slice(1, 7)];
  }, [state.recentCalls]);

  const selectedLead = useMemo(
    () => state.work?.leads.find((lead) => lead.contact_id === state.selectedContactId) ?? null,
    [state.selectedContactId, state.work?.leads],
  );
  const contactCalls = useMemo(
    () => state.contactActivity.filter((entry) => entry.type === 'call'),
    [state.contactActivity],
  );
  const contactNotes = useMemo(
    () => state.contactActivity.filter((entry) => entry.type === 'comment'),
    [state.contactActivity],
  );
  const lastContactCall = contactCalls[0] ?? null;

  useEffect(() => {
    setLeadNote('');
  }, [state.selectedContactId]);

  const saveLeadNote = async () => {
    if (!state.selectedContactId || !leadNote.trim() || savingLeadNote) return;
    setSavingLeadNote(true);
    const saved = await actions.addContactNote(state.selectedContactId, leadNote.trim());
    if (saved) setLeadNote('');
    setSavingLeadNote(false);
  };

  const openCustomerRecord = (contactId: string, historicalCallId?: string) => {
    setMobileSection('call');
    setExpandedPanel(null);
    void actions.selectRecord(contactId, historicalCallId);
  };

  if (state.loading) return <LoadingState label="Loading workspace" />;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {state.staleSince ? (
        <div className="flex h-6 shrink-0 items-center gap-2 border-b border-warn/40 bg-warn-soft px-3 text-xs text-warn">
          Showing data from {formatDateTime(new Date(state.staleSince).toISOString())} — the server is
          unreachable and this view is stale.
        </div>
      ) : null}

      {state.lastError ? (
        <div
          className="flex shrink-0 items-start gap-2 border-b border-danger/40 bg-danger-soft px-3 py-1.5 text-xs text-danger"
          role="alert"
        >
          <strong className="uppercase tracking-wide">{state.lastError.code}</strong>
          <span>{state.lastError.message}</span>
          {typeof state.lastError.details.allowed === 'object' ? (
            <span className="text-faint">
              Allowed: {(state.lastError.details.allowed as string[]).join(', ')}
            </span>
          ) : null}
          {Array.isArray(state.lastError.details.errors) ? (
            <span className="text-faint">
              {(state.lastError.details.errors as Array<{ loc?: unknown[]; msg?: string }>)
                .map((item) => `${item.loc?.join('.')} ${item.msg ?? ''}`.trim())
                .join('; ')}
            </span>
          ) : null}
        </div>
      ) : null}

      {expandedPanel ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-3 sm:p-8" role="dialog" aria-modal="true" aria-label={expandedPanel === 'leads' ? 'All my leads' : 'All recent calls'}>
          <section className="flex h-full max-h-[900px] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-2xl">
            <header className="flex h-14 shrink-0 items-center justify-between border-b border-line bg-surface-2/70 px-4">
              <h2 className="text-base font-bold text-fg">{expandedPanel === 'leads' ? 'My leads' : 'Recent calls'}</h2>
              <Button size="sm" variant="outline" aria-label="Close maximized list" onClick={() => setExpandedPanel(null)}>Close</Button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {expandedPanel === 'leads' ? (
                state.work?.leads.length ? state.work.leads.map((lead) => (
                  <div key={lead.lead_id} className="grid min-h-[72px] w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b border-line/70 px-4 py-3 text-left hover:bg-surface-2 sm:px-6">
                    <button type="button" className="min-w-0 text-left" onClick={() => openCustomerRecord(lead.contact_id)}>
                      <span className="block truncate text-base font-semibold text-ink">{lead.name}</span>
                      <span className="knx-num block text-sm text-faint">{lead.phone_e164}</span>
                      <span className="mt-1 block text-xs text-muted">
                        Assigned {lead.assigned_at ? formatDateTime(lead.assigned_at) : 'date unavailable'}
                      </span>
                      <span className="block text-xs text-faint">
                        {lead.last_called_at
                          ? `Last called ${formatDateTime(lead.last_called_at)}`
                          : 'Not called yet'}
                        {' · '}{lead.attempt_count} attempt{lead.attempt_count === 1 ? '' : 's'}
                      </span>
                    </button>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge tone="neutral">{titleCase(lead.status)}</Badge>
                      <Button size="sm" tone="brand" variant="solid" disabled={!canPlaceOutbound} onClick={() => void actions.dialContact(lead.contact_id)}>Dial</Button>
                    </div>
                  </div>
                )) : <EmptyState message="No leads assigned" hint="Leads appear here once a manager assigns them." />
              ) : (
                recentCallsForPanel.length ? recentCallsForPanel.map((call) => (
                  <div key={call.id} className="grid min-h-[78px] w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b border-line/70 px-4 py-3 text-left hover:bg-surface-2 sm:px-6">
                    <button type="button" className="min-w-0 text-left" disabled={!call.contact_id} onClick={() => { if (call.contact_id) openCustomerRecord(call.contact_id, call.id); }}>
                      <span className="block truncate text-base font-semibold text-ink">{call.customer_name}</span>
                      <span className="knx-num block text-sm text-faint">{call.customer_number}</span>
                      <span className="block truncate text-xs text-muted">{formatDateTime(call.started_at)}</span>
                    </button>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge tone={call.direction === 'inbound' ? 'info' : 'brand'}>{call.direction === 'inbound' ? '↙ Inbound' : '↗ Outbound'}</Badge>
                      <span className="knx-num text-sm font-semibold text-muted">{formatDuration(call.talk_seconds)}</span>
                      <Button size="sm" tone="brand" variant="solid" disabled={!canPlaceOutbound || !call.contact_id} onClick={() => { if (call.contact_id) void actions.dialContact(call.contact_id); }}>Dial</Button>
                    </div>
                  </div>
                )) : <EmptyState message="No calls handled yet" hint="Completed calls appear here." />
              )}
            </div>
          </section>
        </div>
      ) : null}

      <nav
        className="grid shrink-0 grid-cols-4 border-b border-line bg-surface p-1 lg:hidden"
        aria-label="Agent workspace sections"
      >
        {([
          ['dial', 'Dial'],
          ['leads', 'Leads'],
          ['call', state.call ? 'Call' : 'Customer'],
          ['finish', state.agent?.state === 'acw' ? 'Finish' : 'Controls'],
        ] as Array<[MobileSection, string]>).map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-current={mobileSection === id ? 'page' : undefined}
            className={`min-h-11 rounded-md px-1 text-xs font-bold transition-[background-color,color,transform] duration-150 active:scale-[0.97] ${
              mobileSection === id
                ? 'bg-slate-950 text-white shadow-sm'
                : 'text-muted hover:bg-surface-2'
            }`}
            onClick={() => setMobileSection(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-px overflow-y-auto bg-line lg:grid-cols-[340px_minmax(0,1fr)_320px] lg:overflow-hidden dense:lg:grid-cols-[320px_minmax(0,1fr)_300px]">
        {/* ------------------------------------------------------------ left */}
        <div className={`${mobileSection === 'dial' || mobileSection === 'leads' ? 'flex' : 'hidden'} order-1 min-h-0 flex-col gap-px bg-line lg:flex`}>
          <Panel
            title="Outbound dialler"
            actions={
              <Badge tone={dialerBadge.tone}>{dialerBadge.label}</Badge>
            }
            className={`${mobileSection === 'dial' ? 'block' : 'hidden'} shrink-0 lg:block`}
            bodyClassName="overflow-visible"
          >
            <form className="p-3" onSubmit={submitOutbound}>
              <div className="flex gap-2">
                <label className="min-w-0 flex-1">
                  <span className="sr-only">Phone number</span>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={outboundNumber}
                    onChange={(event) => {
                      setOutboundNumber(event.target.value);
                      setDialValidation('');
                    }}
                    placeholder="+14155550123"
                    className="knx-num h-10 w-full rounded-md border border-line-strong bg-surface px-3 text-base text-fg outline-none transition focus:border-focus focus:ring-2 focus:ring-focus/20"
                  />
                </label>
                <Button type="submit" tone="brand" variant="solid" disabled={!canPlaceOutbound}>
                  Dial
                </Button>
              </div>
              <div className={`mt-1.5 text-xs ${dialValidation ? 'text-danger' : 'text-muted'}`}>
                {dialValidation ||
                  (state.agent?.state === 'available'
                    ? 'Calls use the provider selected in Admin.'
                    : 'Set Presence to Available to dial.')}
              </div>
            </form>
          </Panel>
          <Panel
            title="My leads"
            actions={<Button size="sm" variant="ghost" aria-label="Maximize My leads" onClick={() => setExpandedPanel('leads')}>Maximize</Button>}
            className={`${mobileSection === 'leads' ? 'block' : 'hidden'} min-h-48 lg:block lg:h-[30%] lg:min-h-0`}
          >
            {state.work?.leads.length ? (
              state.work.leads.map((lead) => (
                <div
                  key={lead.lead_id}
                  className={`grid min-h-[64px] w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-line/70 px-3 py-2 text-left transition-colors duration-150 ${
                    state.selectedContactId === lead.contact_id && !state.selectedCallId
                      ? 'border-l-4 border-l-brand bg-brand-soft'
                      : 'bg-surface hover:bg-surface-2'
                  }`}
                >
                  <button
                    type="button"
                    aria-pressed={state.selectedContactId === lead.contact_id && !state.selectedCallId}
                    className="min-w-0 text-left"
                    onClick={() => openCustomerRecord(lead.contact_id)}
                  >
                    <span className="block truncate font-semibold text-ink">{lead.name}</span>
                    <span className="knx-num block text-xs text-faint">{lead.phone_e164}</span>
                    <span className="mt-1 block text-xs text-muted">
                      Assigned {lead.assigned_at ? formatDateTime(lead.assigned_at) : 'date unavailable'}
                    </span>
                    <span className="block truncate text-xs text-faint">
                      {lead.last_called_at
                        ? `Last called ${formatDateTime(lead.last_called_at)}`
                        : 'Not called yet'}
                      {' · '}{lead.attempt_count} attempt{lead.attempt_count === 1 ? '' : 's'}
                    </span>
                  </button>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Badge tone="neutral">{titleCase(lead.status)}</Badge>
                    <Button
                      size="sm"
                      tone="brand"
                      variant="solid"
                      disabled={!canPlaceOutbound}
                      title={canPlaceOutbound ? `Dial ${lead.name}` : 'Set Presence to Available to dial'}
                      onClick={() => void actions.dialContact(lead.contact_id)}
                    >
                      Dial
                    </Button>
                    <a
                      className="inline-flex h-8 items-center rounded-md border border-line-strong/60 bg-surface px-2 text-xs font-semibold text-fg shadow-sm hover:border-focus/50 hover:bg-surface-2"
                      href={`sms:${encodeURIComponent(lead.phone_e164)}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      Message
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState message="No leads assigned" hint="Leads appear here once a manager assigns them." />
            )}
          </Panel>
          <Panel
            title="Recent calls"
            actions={<><Badge tone="info">Inbound + outbound</Badge><Button size="sm" variant="ghost" aria-label="Maximize Recent calls" onClick={() => setExpandedPanel('recent')}>Maximize</Button></>}
            className={`${mobileSection === 'leads' ? 'block' : 'hidden'} min-h-56 lg:flex lg:flex-1`}
          >
            {recentCallsForPanel.length ? (
              recentCallsForPanel.map((call) => (
                <div
                  key={call.id}
                  className={`grid min-h-[76px] w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-line/70 px-3 py-2 text-left transition-colors duration-150 ${
                    state.selectedCallId === call.id
                      ? 'border-l-4 border-l-brand bg-brand-soft'
                      : 'bg-surface hover:bg-surface-2'
                  }`}
                >
                  <button
                    type="button"
                    aria-pressed={state.selectedCallId === call.id}
                    disabled={!call.contact_id}
                    className="min-w-0 text-left disabled:cursor-default"
                    onClick={() => {
                      if (call.contact_id) openCustomerRecord(call.contact_id, call.id);
                    }}
                  >
                    <span className="block truncate font-semibold text-ink">{call.customer_name}</span>
                    <span className="knx-num block text-xs text-faint">{call.customer_number}</span>
                    <span className="block truncate text-xs text-muted">
                      {formatDateTime(call.started_at)}
                    </span>
                  </button>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <Badge tone={call.direction === 'inbound' ? 'info' : 'brand'}>
                      {call.direction === 'inbound' ? '↙ Inbound' : '↗ Outbound'}
                    </Badge>
                    <span className="knx-num text-xs font-semibold text-muted">
                      {formatDuration(call.talk_seconds)}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        tone="brand"
                        variant="solid"
                        disabled={!canPlaceOutbound}
                        title={canPlaceOutbound ? `Dial ${call.customer_name}` : 'Set Presence to Available to dial'}
                        onClick={() => {
                          if (call.contact_id) void actions.dialContact(call.contact_id);
                        }}
                      >
                        Dial
                      </Button>
                      <a
                        className="inline-flex h-8 items-center rounded-md border border-line-strong/60 bg-surface px-2 text-xs font-semibold text-fg shadow-sm hover:border-focus/50 hover:bg-surface-2"
                        href={`sms:${encodeURIComponent(call.customer_number)}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        Message
                      </a>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState message="No calls handled yet" hint="Completed calls appear here." />
            )}
          </Panel>
          <Panel title="Callbacks" className={`${mobileSection === 'leads' ? 'block' : 'hidden'} min-h-32 lg:block lg:h-[15%] lg:min-h-0`}>
            {state.work?.callbacks.length ? (
              state.work.callbacks.map((callback) => (
                <Row key={callback.callback_id}>
                  <span className="knx-num flex-1">{callback.phone_e164}</span>
                  <span className="text-xs text-faint">{formatDateTime(callback.scheduled_for)}</span>
                </Row>
              ))
            ) : (
              <EmptyState message="No callbacks scheduled" />
            )}
          </Panel>
        </div>

        {/* ---------------------------------------------------------- centre */}
        <div className={`${mobileSection === 'call' ? 'flex' : 'hidden'} order-3 min-h-0 flex-col gap-px bg-line lg:order-2 lg:flex`}>
          <Panel title={callIsLive ? 'Live call' : state.call ? 'Completed call' : 'Customer record'} className="shrink-0">
            {state.call ? (
              <div className="grid min-h-[190px] grid-cols-1 bg-slate-950 text-white sm:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)]">
                <div className="flex min-w-0 flex-col justify-center border-r border-white/15 px-6 py-5">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${callIsLive ? 'animate-knx-pulse-dot bg-lime-400' : 'bg-slate-400'}`} />
                    <span className="text-xs font-bold uppercase tracking-[0.16em] text-lime-300">
                      {callIsLive ? `Live ${state.call.direction} call` : 'Call ended — wrap-up required'}
                    </span>
                    <Badge tone="brand">{titleCase(state.call.status)}</Badge>
                  </div>
                  <div className="knx-num mt-4 truncate text-3xl font-bold tracking-tight text-white">
                    {liveCustomerNumber}
                  </div>
                  <div className="mt-1 text-base font-semibold text-slate-200">
                    {state.contact?.full_name ?? 'No matching customer record'}
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
                    <span>{state.contact ? 'CRM record matched' : 'Number shown from the live provider call'}</span>
                    <span aria-hidden="true">·</span>
                    <span>Started {formatDateTime(state.call.started_at)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 content-center gap-px bg-white/15">
                  <LiveCallField label="Provider" value={providerName} />
                  <LiveCallField label="Status" value={titleCase(state.call.status)} accent />
                  <LiveCallField label="Caller ID" value={state.call.caller_id_used || state.call.from_e164} numeric />
                  <LiveCallField
                    label={state.call.answered_at ? 'Talk time' : 'Ring time'}
                    value={formatDuration(
                      state.call.answered_at
                        ? state.call.timers.talk_seconds
                        : state.agent?.seconds_in_state ?? state.call.timers.ring_seconds,
                    )}
                    numeric
                  />
                  <div className="col-span-2 flex items-center justify-between gap-4 bg-slate-900 px-4 py-3">
                    <div>
                      <div className="text-2xs font-semibold uppercase tracking-wider text-slate-400">Recording</div>
                      <div className="mt-0.5 text-sm font-semibold text-white">{liveRecordingStatus}</div>
                    </div>
                    {callIsLive ? (
                      <Button
                        tone="danger"
                        variant="solid"
                        disabled={!controlAvailability('hangup', controlContext).enabled}
                        onClick={actions.hangup}
                      >
                        {state.call.status === 'ringing' ? 'Cancel call' : 'Hang up'}
                      </Button>
                    ) : <Badge tone="ok">Completed</Badge>}
                  </div>
                </div>
              </div>
            ) : state.contact ? (
              <div className="bg-surface">
                <div className="flex flex-col gap-4 border-b border-line bg-gradient-to-r from-slate-950 to-slate-900 px-5 py-4 text-white sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-xl font-bold">{state.contact.full_name}</h2>
                      {state.contact.is_vip ? <Badge tone="warn">VIP</Badge> : null}
                      <Badge tone={state.contact.do_not_call ? 'danger' : 'ok'}>
                        {state.contact.do_not_call ? 'Do not call' : 'Callable'}
                      </Badge>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-300">
                      <span className="knx-num font-semibold text-white">
                        {state.contact.phones.find((phone) => phone.is_primary)?.phone_e164 ?? 'No primary number'}
                      </span>
                      <span>{[state.contact.city, state.contact.country].filter(Boolean).join(', ') || 'Location not set'}</span>
                      <span>{state.contact.preferred_language || 'Language not set'}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      tone="brand"
                      variant="solid"
                      disabled={!canPlaceOutbound}
                      onClick={() => void actions.dialContact(state.contact!.id)}
                    >
                      Dial lead
                    </Button>
                    <a
                      className="inline-flex h-10 items-center rounded-md border border-white/20 bg-white/10 px-3 text-sm font-semibold text-white transition-colors hover:bg-white/15 active:scale-[0.97]"
                      href={`sms:${encodeURIComponent(state.contact.phones.find((phone) => phone.is_primary)?.phone_e164 ?? '')}`}
                    >
                      Message
                    </a>
                  </div>
                </div>
                <div className="grid grid-cols-2 divide-x divide-y divide-line sm:grid-cols-4 sm:divide-y-0">
                  <CrmMetric
                    label="Assigned"
                    value={selectedLead?.assigned_at ? formatDateTime(selectedLead.assigned_at) : 'Not available'}
                    hint={selectedLead ? `${titleCase(selectedLead.status)} · Priority ${selectedLead.priority}` : 'Opened from call history'}
                  />
                  <CrmMetric
                    label="Last called"
                    value={lastContactCall?.occurred_at ? formatDateTime(lastContactCall.occurred_at) : 'Not called yet'}
                    hint={lastContactCall?.agent_name || 'No agent yet'}
                  />
                  <CrmMetric
                    label="Last talk time"
                    value={formatDuration(lastContactCall?.talk_seconds ?? 0)}
                    hint={lastContactCall?.disposition || 'No disposition'}
                    numeric
                  />
                  <CrmMetric
                    label="Attempts"
                    value={String(selectedLead?.attempt_count ?? state.contact.phones.find((phone) => phone.is_primary)?.attempt_count ?? 0)}
                    hint={`${contactCalls.length} call${contactCalls.length === 1 ? '' : 's'} in history`}
                    numeric
                  />
                </div>
              </div>
            ) : (
              <EmptyState
                message="Select a lead or recent call"
                hint="The customer record and call history will open here."
              />
            )}
          </Panel>

          <Panel title={callIsLive ? 'Live call events' : 'Lead history & notes'} className="h-[300px] shrink-0">
            {callIsLive && state.timeline.length ? (
              <table className="w-full border-collapse text-base">
                <thead className="sticky top-0 bg-surface-2 text-left text-2xs uppercase tracking-wide text-faint">
                  <tr>
                    <th className="h-7 px-2 font-medium">#</th>
                    <th className="h-7 px-2 font-medium">Event</th>
                    <th className="h-7 px-2 font-medium">Time</th>
                    <th className="h-7 px-2 font-medium">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {state.timeline.map((entry) => (
                    <tr key={entry.sequence} className="h-row-sm border-b border-line/60">
                      <td className="knx-num px-2 text-faint">{entry.sequence}</td>
                      <td className="px-2">{entry.event_type}</td>
                      <td className="knx-num px-2 text-muted">{formatDateTime(entry.occurred_at)}</td>
                      <td className="px-2 text-faint">{entry.actor_type}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : state.contact ? (
              <div className="grid h-full min-h-0 grid-cols-1 md:grid-cols-[minmax(0,1fr)_300px]">
                <div className="min-h-0 overflow-y-auto border-b border-line md:border-b-0 md:border-r">
                  {state.contactActivity.length ? (
                    state.contactActivity.map((entry) => (
                      <LeadActivityRow key={`${entry.type}-${entry.id}`} entry={entry} />
                    ))
                  ) : (
                    <EmptyState message="No lead history yet" hint="Calls, notes and callbacks will appear here." />
                  )}
                </div>
                <div className="flex min-h-0 flex-col bg-surface-2/40 p-3">
                  <div>
                    <h3 className="text-sm font-bold text-fg">Add permanent CRM note</h3>
                    <p className="mt-0.5 text-xs text-muted">
                      Saved against this lead for every agent and future call.
                    </p>
                  </div>
                  {selectedLead?.initial_notes ? (
                    <div className="mt-2 rounded-md border border-line bg-surface px-2.5 py-2 text-xs text-muted">
                      <span className="font-semibold text-fg">Imported note: </span>{selectedLead.initial_notes}
                    </div>
                  ) : null}
                  <label className="mt-2 flex min-h-0 flex-1 flex-col" htmlFor="lead-note">
                    <span className="sr-only">New lead note</span>
                    <textarea
                      id="lead-note"
                      className="min-h-[92px] flex-1 resize-none rounded-md border border-line-strong bg-surface p-3 text-base outline-none transition-[border-color,box-shadow] focus:border-focus focus:ring-2 focus:ring-focus/20"
                      placeholder="Write what the next agent must know…"
                      value={leadNote}
                      onChange={(event) => setLeadNote(event.target.value)}
                    />
                  </label>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="text-xs text-faint">{contactNotes.length} saved note{contactNotes.length === 1 ? '' : 's'}</span>
                    <Button
                      size="sm"
                      tone="brand"
                      variant="solid"
                      disabled={!leadNote.trim() || savingLeadNote}
                      onClick={() => void saveLeadNote()}
                    >
                      {savingLeadNote ? 'Saving…' : 'Save note'}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyState
                message="No lead selected"
                hint="Choose a lead or recent call to inspect its complete CRM history."
              />
            )}
          </Panel>

          <Panel
            title={<span className={state.agent?.state === 'acw' || Boolean(state.call && isTerminalCallStatus(state.call.status)) ? 'animate-pulse text-danger' : ''}>Wrap-up</span>}
            className="min-h-[250px] flex-1"
          >
            <div className="grid gap-3 p-3 sm:grid-cols-[minmax(180px,0.35fr)_minmax(0,1fr)]">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-fg" htmlFor="disposition">Disposition (required)</label>
                <select id="disposition" className="h-10 rounded-md border border-line bg-surface px-2 text-base" value={dispositionId} onChange={(event) => setDispositionId(event.target.value)}>
                  <option value="">Select a disposition…</option>
                  {state.dispositions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
                {selectedDisposition?.requires_notes ? <p className="text-xs text-warn">This disposition requires notes.</p> : null}
                {selectedDisposition?.requires_callback ? (
                  <>
                    <label className="text-xs text-warn" htmlFor="callback-at">Callback time (required)</label>
                    <input id="callback-at" type="datetime-local" className="h-10 rounded-md border border-line bg-surface px-2 text-base" value={callbackAt} onChange={(event) => setCallbackAt(event.target.value)} />
                  </>
                ) : null}
                <ControlButton
                  label="Submit disposition"
                  tone="brand"
                  availability={controlAvailability('disposition', controlContext)}
                  disabled={!dispositionId}
                  onClick={() => actions.submitDisposition({ disposition_id: dispositionId, notes, callback_at: callbackAt ? new Date(callbackAt).toISOString() : null })}
                />
              </div>
              <label className="flex min-h-[170px] flex-col gap-2 text-sm font-semibold text-fg" htmlFor="wrap-up-notes">
                Notes
                <textarea id="wrap-up-notes" aria-label="Wrap-up notes" className="min-h-[170px] flex-1 resize-y rounded-md border border-line bg-surface p-3 text-base" placeholder="Add call notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
              </label>
            </div>
          </Panel>
        </div>

        {/* ----------------------------------------------------------- right */}
        <div className={`${mobileSection === 'dial' || mobileSection === 'finish' ? 'flex' : 'hidden'} order-2 min-h-0 flex-col gap-px bg-line lg:order-3 lg:flex`}>
          <Panel title="Presence" className={`${mobileSection === 'dial' ? 'block' : 'hidden'} shrink-0 lg:block`}>
            <div className="flex flex-wrap gap-1 p-2">
              <Button
                size="sm"
                tone="ok"
                variant={state.agent?.state === 'available' ? 'solid' : 'outline'}
                disabled={state.agent?.state !== 'available' && !state.agent?.allowed_transitions.includes('available')}
                onClick={() => actions.setAgentState('available')}
              >
                Available
              </Button>
              {BREAK_STATES.map((breakState) => (
                <Button
                  key={breakState}
                  size="sm"
                  tone={state.agent?.state === breakState ? 'warn' : 'neutral'}
                  variant={state.agent?.state === breakState ? 'solid' : 'outline'}
                  disabled={state.agent?.state !== breakState && !state.agent?.allowed_transitions.includes(breakState as never)}
                  onClick={() => actions.setAgentState(breakState)}
                >
                  {titleCase(breakState)}
                </Button>
              ))}
              <Button
                size="sm"
                disabled={!state.agent?.allowed_transitions.includes('offline')}
                onClick={() => actions.setAgentState('offline')}
              >
                Sign off
              </Button>
            </div>
          </Panel>

          <Panel title="Transfer and conference" className={`${mobileSection === 'finish' ? 'block' : 'hidden'} shrink-0 lg:block`}>
            <div className="flex flex-col gap-1.5 p-2">
              {state.call && !providerCaps.includes('hold') ? (
                <p className="rounded-md border border-warn/40 bg-warn-soft p-2 text-xs text-warn">
                  {providerName} does not expose web hold, mute or transfer controls for this PSTN call. Hang up remains available; unavailable controls are disabled rather than pretending to work.
                </p>
              ) : null}
              <label className="text-xs text-faint" htmlFor="transfer-target">
                Target agent
              </label>
              <select
                id="transfer-target"
                className="h-8 rounded-md border border-line bg-surface px-2 text-base"
                value={transferTarget}
                onChange={(event) => setTransferTarget(event.target.value)}
              >
                <option value="">Select an agent…</option>
                {state.directory
                  .filter((entry) => entry.user_id !== user?.user_id)
                  .map((entry) => (
                    <option key={entry.user_id} value={entry.user_id} disabled={!entry.available}>
                      {entry.full_name}{entry.extension ? ` · Ext ${entry.extension}` : ''} — {entry.state}
                    </option>
                  ))}
              </select>
              <div className="flex gap-1">
                <ControlButton
                  label="Warm transfer"
                  availability={controlAvailability('warm_transfer', controlContext)}
                  disabled={!transferTarget}
                  onClick={async () => {
                    await actions.startTransfer(transferTarget, true);
                    setConsultLegId('pending');
                  }}
                />
                <ControlButton
                  label="Blind transfer"
                  availability={controlAvailability('blind_transfer', controlContext)}
                  disabled={!transferTarget}
                  onClick={() => actions.startTransfer(transferTarget, false)}
                />
              </div>
              {consultLegId ? (
                <Button
                  size="sm"
                  tone="brand"
                  variant="solid"
                  onClick={() => {
                    const leg = state.call?.legs.find((item) => item.role === 'transfer_target');
                    if (leg) {
                      void actions.completeTransfer(leg.id);
                      setConsultLegId(null);
                    }
                  }}
                >
                  Complete warm transfer
                </Button>
              ) : null}
            </div>
          </Panel>

          <Panel title="Wrap-up" className="hidden">
            <div className="flex flex-col gap-2 p-2">
              <label className="text-xs text-faint" htmlFor="disposition">
                Disposition (required)
              </label>
              <select
                id="disposition"
                className="h-8 rounded-md border border-line bg-surface px-2 text-base"
                value={dispositionId}
                onChange={(event) => setDispositionId(event.target.value)}
              >
                <option value="">Select a disposition…</option>
                {state.dispositions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>

              {selectedDisposition?.requires_notes ? (
                <p className="text-2xs text-warn">This disposition requires notes.</p>
              ) : null}
              <textarea
                aria-label="Wrap-up notes"
                className="min-h-[72px] rounded-md border border-line bg-surface p-2 text-base"
                placeholder="Notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />

              {selectedDisposition?.requires_callback ? (
                <>
                  <label className="text-xs text-warn" htmlFor="callback-at">
                    Callback time (required)
                  </label>
                  <input
                    id="callback-at"
                    type="datetime-local"
                    className="h-8 rounded-md border border-line bg-surface px-2 text-base"
                    value={callbackAt}
                    onChange={(event) => setCallbackAt(event.target.value)}
                  />
                </>
              ) : null}

              <ControlButton
                label="Submit disposition"
                tone="brand"
                availability={controlAvailability('disposition', controlContext)}
                disabled={!dispositionId}
                onClick={() =>
                  actions.submitDisposition({
                    disposition_id: dispositionId,
                    notes,
                    callback_at: callbackAt ? new Date(callbackAt).toISOString() : null,
                  })
                }
              />
            </div>
          </Panel>

          <Panel title="Today" className={`${mobileSection === 'finish' ? 'block' : 'hidden'} shrink-0 lg:block`}>
            <div className="grid grid-cols-2">
              <Field label="Calls handled">
                <span className="knx-num">{state.agent?.calls_handled_today ?? 0}</span>
              </Field>
              <Field label="Talk time">
                <span className="knx-num">{formatDuration(state.agent?.talk_seconds_today ?? 0)}</span>
              </Field>
              <Field label="Wrap-up time">
                <span className="knx-num">{formatDuration(state.agent?.acw_seconds_today ?? 0)}</span>
              </Field>
              <Field label="Available time">
                <span className="knx-num">
                  {formatDuration(state.agent?.available_seconds_today ?? 0)}
                </span>
              </Field>
            </div>
          </Panel>
        </div>
      </div>

      <CallBar
        agent={state.agent}
        call={state.call}
        controlContext={controlContext}
        connection={state.connection}
        onAnswer={actions.answer}
        onHold={actions.hold}
        onResume={actions.resume}
        onHangup={actions.hangup}
        onMute={actions.mute}
        onCompleteAcw={actions.completeAcw}
      />
    </div>
  );
}

function LiveCallField({
  label,
  value,
  accent = false,
  numeric = false,
}: {
  label: string;
  value: string;
  accent?: boolean;
  numeric?: boolean;
}) {
  return (
    <div className="bg-slate-900 px-4 py-3">
      <div className="text-2xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div
        className={`${numeric ? 'knx-num ' : ''}mt-1 truncate text-sm font-bold ${accent ? 'text-lime-300' : 'text-white'}`}
      >
        {value}
      </div>
    </div>
  );
}

function CrmMetric({
  label,
  value,
  hint,
  numeric = false,
}: {
  label: string;
  value: string;
  hint: string;
  numeric?: boolean;
}) {
  return (
    <div className="min-w-0 px-4 py-3">
      <div className="text-2xs font-bold uppercase tracking-[0.14em] text-faint">{label}</div>
      <div className={`${numeric ? 'knx-num ' : ''}mt-1 truncate text-sm font-bold text-fg`} title={value}>
        {value}
      </div>
      <div className="mt-0.5 truncate text-xs text-muted" title={hint}>{hint}</div>
    </div>
  );
}

function LeadActivityRow({ entry }: { entry: ActivityEntry }) {
  const occurredAt = entry.occurred_at ? formatDateTime(entry.occurred_at) : 'Time unavailable';

  if (entry.type === 'call') {
    return (
      <article className="border-b border-line/70 px-4 py-3 last:border-b-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={entry.direction === 'inbound' ? 'info' : 'brand'}>
                {titleCase(entry.direction ?? 'call')} call
              </Badge>
              <span className="truncate text-sm font-bold text-fg">{entry.agent_name || 'Unassigned agent'}</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted">
              <span>{occurredAt}</span>
              <span className="knx-num">Talk {formatDuration(entry.talk_seconds ?? 0)}</span>
              <span className="knx-num">Handle {formatDuration(entry.handle_seconds ?? 0)}</span>
            </div>
          </div>
          <span className="shrink-0 text-xs font-semibold text-faint">{entry.disposition || titleCase(entry.status ?? 'completed')}</span>
        </div>
        {entry.notes ? (
          <p className="mt-2 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm leading-relaxed text-fg">
            {entry.notes}
          </p>
        ) : null}
      </article>
    );
  }

  if (entry.type === 'comment') {
    return (
      <article className="border-b border-line/70 bg-brand-soft/30 px-4 py-3 last:border-b-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Badge tone="neutral">CRM note</Badge>
            <span className="text-sm font-semibold text-fg">{entry.author_name || 'System'}</span>
          </div>
          <span className="shrink-0 text-xs text-faint">{occurredAt}</span>
        </div>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-fg">{entry.body || 'Empty note'}</p>
      </article>
    );
  }

  if (entry.type === 'callback') {
    return (
      <article className="border-b border-line/70 px-4 py-3 last:border-b-0">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-fg">Callback scheduled</div>
            <div className="mt-0.5 text-xs text-muted">
              For {entry.scheduled_for ? formatDateTime(entry.scheduled_for) : 'time unavailable'}
            </div>
          </div>
          <Badge tone="warn">Callback</Badge>
        </div>
      </article>
    );
  }

  return (
    <article className="flex items-center justify-between gap-3 border-b border-line/70 px-4 py-3 last:border-b-0">
      <div>
        <div className="text-sm font-bold text-fg">{titleCase(entry.type)} updated</div>
        <div className="mt-0.5 text-xs text-muted">{occurredAt}</div>
      </div>
      <Badge tone="neutral">{titleCase(entry.status ?? entry.type)}</Badge>
    </article>
  );
}

function ControlButton({
  label,
  availability,
  onClick,
  disabled,
  tone,
}: {
  label: string;
  availability: { enabled: boolean; reason?: string };
  onClick: () => void;
  disabled?: boolean;
  tone?: 'brand';
}) {
  const blocked = !availability.enabled || disabled;
  return (
    <Button
      size="sm"
      tone={tone ?? 'neutral'}
      variant={tone ? 'solid' : 'outline'}
      disabled={blocked}
      title={availability.enabled ? label : availability.reason}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
